import axios from 'axios';
import { Auth, API } from 'aws-amplify';
import { Signer, DateUtils } from '@aws-amplify/core';
import moment from 'moment';
import store from '@/store';
import * as config from '@/config';

import * as cacheSystem from '@/library/utils/cacheSystem'

const URL_API_HORA = config.URL_API_HORA;

// Útiles
export const executeJSONSignedRequest = function(method, url, data, thenFun, catchFun) {
	// Hace lo mismo que executeSignedRequest, pero aplica JSON.stringify a data antes
	let stringifiedData = (data !== undefined) ? JSON.stringify(data) : undefined;
	executeSignedRequest(method, url, stringifiedData, thenFun, catchFun);
};

export const executeSignedRequest = function(method, url, data, thenFun, catchFun, retryWithClockOffset) {
	// De ser necesario, data puede ser undefined (por ejemplo, en un GET)
	// Por defecto, si petición falla, reintentar con hora corregida.
	retryWithClockOffset = (retryWithClockOffset === undefined) ? true : retryWithClockOffset;
	
	let exitoAuth = function(sessionObject) {
		let sessionToken = sessionObject.sessionToken;
		let accessKeyId = sessionObject.accessKeyId;
		let secretAccessKey = sessionObject.secretAccessKey;

		// Usar Signer para firmar petición. Para el proceso se usa la hora LOCAL.
		// Véase https://aws-amplify.github.io/amplify-js/api/classes/signer.html
		let request = {
			method,
			url,
			data
		};
		
		let accessInfo = {
			access_key: accessKeyId, 
			secret_key: secretAccessKey,
			session_token: sessionToken,
		};
		
		let serviceInfo = null;
		
		// https://stackoverflow.com/questions/60554364/authenticate-to-aws-with-axios-and-aws4
		let signedRequest = Signer.sign(request, accessInfo, serviceInfo);
		delete signedRequest.headers['host'];
		let axiosInstance = axios.create();
		
		axiosInstance(signedRequest).then(function(response) {
			// Petición tuvo éxito, ejecutar callback then
			thenFun(response);
		})
		.catch(function(err) {
			// Petición falló.
			// Ver si posiblemente falló con 403. Si es así y si se pidió reintentar con
			// hora corregida, reintentar.
			let isNetworkError = (err.toString() === 'Error: Request failed with status code 403');
			if(isNetworkError && retryWithClockOffset) {
				// Reintentar corrigiendo hora
				let exitoTime = function(millisEpoch) {
					// Obtener hora local y remota. Calcular diferencia para que al momento de la firma
					// se compense la diferencia.
					let remoteTimeMillisEpoch = Number(moment(millisEpoch).format('x'));
					let localTimeMillisEpoch = Number(moment().format('x'));
					let clockOffset = remoteTimeMillisEpoch - localTimeMillisEpoch;
					// Establecer offset. Es un valor global: Signer.sign usa DateUtils.getClockOffset().
					DateUtils.setClockOffset(clockOffset);
					// Reintentar. CUIDADO, en esta llamada a executeSignedRequest se DEBE establecer 
					// retryWithClockOffset=false para evitar recursión sin fin.
					executeSignedRequest(method, url, data, thenFun, catchFun, false);
				};
				
				// Obtener tiempo remoto.
				axios.get(URL_API_HORA).
				then(function(res) {
					let millisEpoch = res.data.requestTimeEpoch;
					exitoTime(millisEpoch);
				}).
				catch(function(err) {
					catchFun('Error al hacer petición, posiblemente hora local sea incorrecta. Pruebe corregirla e intente nuevamente.');
				});
			}
			else {
				// NO reintentar, fallar de una vez!
				catchFun(err);
			}
		});
	};
	
	let errorAuth = function(err) {
		catchFun(`Error al obtener credenciales para firmar petición`);
	};

	getCurrentCredentialsRelogin().then(resp=>{

		// se centraliza la llamada a las credenciales por medio de una funcion
		// que retorna una promesa con las credenciales del usuario para
		// AWS -- 21-08-20
		// Auth.currentCredentials().then(exitoAuth).catch(errorAuth);
		store.dispatch('usuario/getAWSCredentials').then(exitoAuth).catch(errorAuth);
	}).catch(err=>{
		store.dispatch('usuario/onSessionExpired', err);
	});
	
};

export const federatedSignIn = async function(authToken, user, jwt) {

	const expireTime = authToken.expires_at;
	
	let resp = await Auth.federatedSignIn('developer', authToken, user);
	cacheSystem.setItem('user', btoa(JSON.stringify(user)), expireTime);
	cacheSystem.setItem('sessionToken', btoa(JSON.stringify(authToken)), expireTime);
	setAuthTokens(jwt.accessToken, jwt.refreshToken);

	return resp;
}

/**
 * 
 * @param {string} access 
 * @param {string} refresh 
 * @param {number} aTTTL access token TTL en segundos
 * @param {*} rTTTL refresh token TTL en segundos
 */
function setAuthTokens(access, refresh, aTTTL, rTTTL){

	const MINS_IN_HOUR = 60, SECS_IN_MIN = 60;
	const currTime = new Date().getTime();

	if( !aTTTL ){
		aTTTL = (12*MINS_IN_HOUR*SECS_IN_MIN*1000)+currTime 
	}
	if( !rTTTL ){
		rTTTL = (7*24*MINS_IN_HOUR*SECS_IN_MIN*1000)+currTime 
	}

	cacheSystem.setItem('accessToken', access, aTTTL); 
	cacheSystem.setItem('refreshToken', refresh, rTTTL);

}

export const getCurrentCredentialsRelogin = async function(){

	async function relogin(resp){
		let user = cacheSystem.getItem('user');
		let authToken = cacheSystem.getItem('sessionToken');

		if(user && authToken){

			const userData = JSON.parse(atob(user));
			let username = userData.username;

			store.state.usuario.username = username;

			await Auth.federatedSignIn(
					'developer',
					JSON.parse(atob(authToken)),
					userData
				);
			return await Auth.currentCredentials();
		}else{
			
			throw resp
		}
	}

	async function refreshSessionIfNeeded(){

		let at = cacheSystem.getItem('accessToken');
		let rt = cacheSystem.getItem('refreshToken');

		const federatedInfo = cacheSystem.getFederatedInfo();
		const jwtTokenShouldRefresh = at === null && rt != null;

		if( jwtTokenShouldRefresh || !federatedInfo ){

			let resp = await refreshToken();
			return resp;
		}

		return;
	}

	try{

		let federatedInfo = cacheSystem.getFederatedInfo();

		await refreshSessionIfNeeded();

		let resp = await Auth.currentCredentials();

		if( resp.name === 'NotAuthorizedException'){
			
			return await relogin(resp)
		}
		return resp;
	}catch(err){

		throw err;
	}
}

export const logout = async function(){
	await Auth.signOut();
	cacheSystem.clearCache();
}

export async function refreshToken(){

	let accessToken = cacheSystem.getItem('accessToken');
	let refreshToken = cacheSystem.getItem('refreshToken');

	let federatedInfo = cacheSystem.getFederatedInfo();
	
	if( accessToken !== null && !!federatedInfo ){
		return accessToken;
	}

	if( refreshToken === null ){
		throw {statusCode: 401, message: 'Sesión Expirada'};
	}


	let params = {
		body:{
			refreshToken,
			accessToken,
		}
	};

	let { 
		openId, expires_in, user, jwt
	} = await API.post('apiAuthSII', '/refresh-token', params);

	store.state.usuario.username = user.username;

	let authToken = {
		token: openId.Token,
		identity_id: openId.IdentityId, // Optional
		expires_at: new Date(expires_in) - new Date() // the expiration timestamp
	}

	let resp = await federatedSignIn(authToken, user, jwt);

	return jwt;
}
